import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { DEFAULT_COMPANY } from '../config/CompanyDetails';

// ============================================
// STYLES
// ============================================
const styles = StyleSheet.create({
  page: {
    padding: 0,
    backgroundColor: '#FFFFFF',
    fontFamily: 'Helvetica',
    position: 'relative',
    minHeight: '100%',
  },
  header: {
    width: '100%',
    backgroundColor: '#0A1929',
    padding: 12,
    paddingBottom: 8,
    marginBottom: 8,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    flexDirection: 'column',
  },
  headerLogo: {
    width: 120,
    height: 50,
    objectFit: 'contain',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  headerSub: {
    fontSize: 10,
    color: '#CCCCCC',
    marginTop: 2,
  },
  headerDetails: {
    textAlign: 'right',
  },
  headerDetailText: {
    fontSize: 9,
    color: '#FFFFFF',
    marginBottom: 2,
  },
  quoteTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0A1929',
    textAlign: 'center',
    marginBottom: 8,
    letterSpacing: 2,
  },
  content: {
    paddingHorizontal: 30,
    paddingTop: 30,
    paddingBottom: 50,
    zIndex: 1,
    flex: 1,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
    padding: 6,
    backgroundColor: '#F5F7FA',
    borderRadius: 4,
  },
  infoItem: {
    flexDirection: 'column',
  },
  infoLabel: {
    fontSize: 8,
    color: '#666666',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  infoValue: {
    fontSize: 11,
    color: '#0A1929',
    fontWeight: 'bold',
    marginTop: 2,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#E8ECEF',
    marginVertical: 6,
  },
  twoCol: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 8,
  },
  col: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#0A1929',
    marginBottom: 5,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  sectionText: {
    fontSize: 9,
    color: '#333333',
    marginBottom: 1,
  },
  table: {
    marginVertical: 6,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#0A1929',
    padding: 8,
    borderRadius: 4,
  },
  tableHeaderText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#FFFFFF',
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  tableRowEven: {
    flexDirection: 'row',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
    backgroundColor: '#F9FAFB',
  },
  colDescription: { flex: 3 },
  colQty: { flex: 1, textAlign: 'center' },
  colRate: { flex: 1, textAlign: 'right' },
  colAmount: { flex: 1.5, textAlign: 'right' },
  tableText: {
    fontSize: 9,
    color: '#333333',
  },
  totalsBox: {
    marginTop: 10,
    padding: 15,
    backgroundColor: '#F5F7FA',
    borderRadius: 4,
    alignItems: 'flex-end',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 3,
  },
  totalLabel: {
    fontSize: 9,
    color: '#666666',
    width: 100,
    textAlign: 'right',
  },
  totalValue: {
    fontSize: 9,
    color: '#0A1929',
    width: 100,
    textAlign: 'right',
    fontWeight: 'medium',
  },
  totalRowFinal: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 5,
    paddingTop: 5,
    borderTopWidth: 2,
    borderTopColor: '#0A1929',
  },
  totalLabelFinal: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#0A1929',
    width: 100,
    textAlign: 'right',
  },
  totalValueFinal: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#00A86B',
    width: 100,
    textAlign: 'right',
  },
  routeBox: {
    marginVertical: 4,
    padding: 6,
    backgroundColor: '#F0F8FF',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E0E8F0',
  },
  routeText: {
    fontSize: 9,
    color: '#333333',
    marginBottom: 2,
  },
  paymentBox: {
    marginTop: 15,
    marginBottom: 20,
    padding: 10,
    backgroundColor: '#F0FDF4',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#BBF7D0',
    alignSelf: 'flex-start',
    width: '40%',
  },
  paymentTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#166534',
    marginBottom: 4,
  },
  paymentText: {
    fontSize: 8,
    color: '#166534',
    marginBottom: 1,
  },
  termsPage: {
    paddingTop: 30,
    paddingBottom: 50,
    paddingHorizontal: 0,
    backgroundColor: '#FFFFFF',
    fontFamily: 'Helvetica',
    position: 'relative',
    minHeight: '100%',
  },
  termsContent: {
    paddingHorizontal: 30,
    zIndex: 1,
    flex: 1,
  },
  termsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0A1929',
    textAlign: 'center',
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  termsSection: {
    marginBottom: 8,
  },
  termsSectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#0A1929',
    marginBottom: 8,
  },
  termsText: {
    fontSize: 9,
    color: '#333333',
    lineHeight: 1.5,
    marginBottom: 1,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#E8ECEF',
    paddingTop: 10,
  },
  footerText: {
    fontSize: 7,
    color: '#999999',
    textAlign: 'center',
    marginBottom: 2,
  },
});

// ============================================
// HELPERS
// ============================================
const formatCurrency = (amount) => {
  if (!amount) return 'R 0.00';
  return `R ${parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getTypeLabel = (type) => {
  const labels = { 'abnormal': 'Abnormal Load', 'minerals': 'Minerals', 'agri': 'Agricultural', 'general': 'General' };
  return labels[type] || type;
};

const getTruckTypeLabel = (type) => {
  const labels = { 'side_tipper': 'Side Tipper', 'flat_deck': 'Flat Deck', 'lowbed': 'Lowbed', 'tautliner': 'Tautliner', 'grain_side_tipper': 'Grain Side Tipper', 'extended_trailer': 'Extended Trailer', 'step_deck': 'Step Deck', 'other': 'Other' };
  return labels[type] || type;
};

// ============================================
// TERMS & CONDITIONS
// ============================================
const standardTerms = [
  '1. All services are provided in accordance with One Purpose Logistics standard terms.',
  '2. Payment is due within the agreed payment terms.',
  '3. Any disputes must be raised in writing within 7 days of invoice date.',
  '4. One Purpose Logistics reserves the right to suspend services for non-payment.',
  '5. All goods are carried at the customer\'s risk.',
  '6. The customer indemnifies One Purpose Logistics against any loss or damage.',
  '7. The customer warrants that all documentation submitted is accurate and up-to-date.',
  '8. One Purpose Logistics is not liable for any consequential loss or damage.',
  '9. The customer agrees to comply with all applicable laws and regulations.',
  '10. These terms constitute the entire agreement between the parties.'
];

const abnormalTerms = [
  '1. These terms apply specifically to Abnormal Load transport.',
  '2. Specialized equipment and escorts may be required.',
  '3. Route surveys and permits are the responsibility of the customer.',
  '4. Additional costs for route deviations will be for the customer\'s account.',
  '5. One Purpose Logistics will not be liable for delays caused by permit issues.',
  '6. The customer must provide accurate dimensions and weight of the load.',
  '7. Escort services are quoted separately and may vary based on route conditions.',
  '8. Road closures or restrictions may affect delivery timelines.',
  '9. Abnormal load movement is subject to road traffic regulations.',
  '10. The customer accepts all risks associated with abnormal load transport.'
];

const enmTerms = [
  '1. LOADING & OFFLOADING',
  '1.1 We allow 1 hour for loading and 1 hour for offloading, thereafter standing time will be charged at R1,120.00 (excluding VAT) per hour.',
  '1.2 We require unrestricted access for heavy vehicles at both the loading and offloading points.',
  '1.3 For whatever reason we cannot offload, a futile trip will be charged in full. Futile Trip Costs / Standing Time Costs are managed per event type and contributing factors.',
  '1.4 Should the client book a requested date for transport, it is the client\'s full responsibility to communicate and arrange with their client on the booking made.',
  '1.5 Surfaces should be prepared in advance and must be capable of maintaining the structural integrity of the truck, crane, trailer, and/or container under load. It is the client\'s responsibility to notify us before delivery of the site conditions, i.e., uneven ground, rocky, muddy, low hanging trees, etc. One Purpose Logistics has the right to pull the truck off site should the drivers advise us that the site is inadequately prepared. Full costs will be charged to the client. One Purpose Logistics will not be held liable for any damages caused to the property or surrounds, by our cranes, trucks, and trailers. It is the customer\'s responsibility to arrange that sufficient insurance cover is in place if any damages are caused.',
  '',
  '2. RATES & QUOTATIONS',
  '2.1 Rates do not include weekends and/or public holidays unless specified.',
  '2.2 Rates do not include specialised rigging unless specified.',
  '2.3 Rates do not include any abnormality unless specified.',
  '2.4 Rates do not include special entry permits.',
  '2.5 Quotations are based upon the dimensions/product type and all relevant information supplied by the Customer. In the event of any changes to these, we reserve the right to amend our prices and quotation accordingly.',
  '2.6 Repositioning will be quoted for separately and needs to be approved by our offices. This will be at an extra cost to the client.',
  '',
  '3. ADDITIONAL CHARGES',
  '3.1 Should a specialised safety file, medicals, inductions, and tool tagging be required, this will be at an additional cost to the client of R1,750.00 (excluding VAT).',
  '3.2 When delivering to Mines, Collieries, Power Stations, a surcharge will be charged to the customer of R1,500.00 (excluding VAT).',
  '3.3 A cancellation fee will be charged if transport is cancelled on the day of delivery.',
  '',
  '4. SIDELOADER SPECIFICATIONS',
  '4.1 If quoted on a side lifter, please ensure the site is clear for the side lifter as it can only load and offload from the driver side and must be able to park directly next to and parallel to the container. The sideloader cannot reach out to lift or place a container.',
  '4.2 In order for us to load with a sideloader, the container cannot have any protrusions.',
  '4.3 Sideloader payload – max 27 tons.',
  '',
  '5. CROSS-BORDER TRANSPORT',
  '5.1 Duties & taxes need to be paid upfront by the client before the truck can be dispatched.',
  '5.2 We allow 24hrs @ the following borders for border clearance, thereafter standing time of R6,500.00 (excluding VAT), per day, per truck will be applicable – Zimbabwe, Mozambique, Botswana, Namibia, Swaziland & Lesotho.',
  '5.3 COVID-19 testing / delays may result in additional charges.',
  '5.4 Port of entry clearance must be arranged before truck is dispatched.',
  '5.5 Border documents are for your account and must be handed to the driver on the day of loading.',
  '',
  '6. FIRST-TIME / COD CUSTOMERS',
  '6.1 First-time customer / COD customer, full payment is required upfront if you have no account with One Purpose Logistics.',
  '',
  '7. INSURANCE',
  '7.1 GIT Cover: One Purpose Logistics is covered up to a max of R2.5 million. Where the value of goods to be insured is more, this is not covered. If additional cover is required, the client needs to make special arrangements with One Purpose Logistics offices and the cost will be for the customer\'s account.',
  '7.2 GIT cover detail is available on request.',
  '7.3 In the event of an accident, fire, or theft, the Client is liable for excess payable. Excess amount will be 15% of the claim or a minimum of R20,000.00.',
  '7.4 Contractors All Risk cover is not included. This cover can be arranged on request and is for the customer\'s account.',
  '7.5 It remains the client\'s responsibility to notify One Purpose Logistics in writing of any structural adjustments to the product which could intentionally/unintentionally cause disintegration/damage/harm while loading/offloading and while in transit, and supply One Purpose Logistics with the requisite engineers\' drawings signing off the structural changes. In such aforementioned instance, One Purpose Logistics / their insurer will not be held liable for any claims should the cargo be damaged or compromised before loading, offloading and in transit.',
  '7.6 All claim submissions are subject to approval upon completion of an investigation by our nominated vendor.',
  '7.7 Claims submissions must be submitted within a 48-hour period.',
  '7.8 Should the value of the items exceed our GIT cover amount, a revised quotation for the full amount will be obtained on behalf of the customer from our insurers.',
  '7.9 Rigging cover is not included nor covered. Such cover can be arranged on request and is for the customer\'s account. Rigging cover requires approval prior to the project being initiated.',
  '7.10 Consequential loss of any type is not covered by One Purpose Logistics.',
  '7.11 Structural integrity of all sites remains the responsibility of the customer.',
  '',
  '8. LIABILITY',
  '8.1 One Purpose Logistics will not be held liable for any damages caused to the property or surrounds, by our cranes, trucks, and trailers. It is the customer\'s responsibility to arrange that sufficient insurance cover is in place if any damages are caused.'
];

const beneckeTerms = [
  'TERMS & CONDITIONS – ONE PURPOSE LOGISTICS',
  '',
  '1. Access & Site Requirements',
  'Please ensure clear and unrestricted access for our vehicle/s at both the loading and offloading points.',
  'Side lifter can only load and offload from the driver side and must be able to pull up right next to the container in order to load.',
  'One Purpose Logistics cannot accept liability for surfaces pointed out to our operational staff to place containers. These surfaces should be prepared in advance and must be capable of maintaining the structural integrity of the container under load.',
  '',
  '2. Loading & Offloading',
  'We allow 1 hour for loading and 1 hour for offloading. Thereafter, standing time will be applicable.',
  'Rate does not include weekends and/or public holidays.',
  'No specialised rigging included in this quote.',
  'Rate does not include any abnormality.',
  'Rate subject to availability of vehicle/s.',
  '',
  '3. Quotations & Pricing',
  'This quotation is based upon the dimensions/product type and all relevant information supplied by you. In the event of any changes to these, we reserve the right to amend our prices and quotation accordingly.',
  'When quoted on a daily rate, the hours are from 08:00 – 17:00, and includes 150km round trip.',
  '',
  '4. Cancellation',
  'Cancellation fee of 100% will be charged if transport is cancelled on the day of delivery.',
  '',
  '5. Insurance',
  'GIT Cover: We are covered up to a maximum of R1,000,000. Where the value of goods to be insured exceeds this amount, it is not covered. If additional cover is required, it must be via special arrangement and the cost will be for the customer’s account.',
  'Machine moving under hook crane insurance needs to be confirmed by client prior to loading.',
  '',
  '6. Payment',
  'Please note that upfront payment is required should no account be in place.',
  '',
  '7. Cross-Border Transport',
  'We allow 1 day standing at the border for clearance. Thereafter, standing time of R9,500 per day will apply.',
  'Client to arrange border clearing – we can assist.',
  'Border taxes and clearing are for the client’s account.'
];

const lovemoreTerms = [
  '1. PRICING & QUOTATIONS',
  'Pricing is based on dimensions supplied by the client.',
  'Pricing is based on the job being executed over one week. Additional days will be charged at daily rates.',
  'All prices stated are exclusive of VAT.',
  '',
  '2. ACCESS & SITE REQUIREMENTS',
  'Clear access to loading and offloading areas must be provided by the customer.',
  'Surfaces must be prepared in advance and must be capable of maintaining the structural integrity of the truck.',
  'One Purpose Logistics reserves the right to withdraw the truck from site should the driver advise that the site is inadequately prepared.',
  '',
  '3. OPERATING HOURS',
  'Summer: Monday to Friday, 06h00 to 18h00',
  'Winter: Monday to Friday, 07h00 to 17h00',
  '',
  '4. LOADING & OFFLOADING',
  'For transport-only jobs, 2 hours are allowed for loading and offloading. Thereafter, standing time will be charged.',
  'If for any reason we cannot offload, a futile trip will be charged in full.',
  '',
  '5. STANDING TIME & DELAYS',
  'Price excludes any standing time charges for any delays not caused by One Purpose Logistics.',
  'One Purpose Logistics will not be liable for any claims emanating from delays due to labour strikes, adverse weather, force majeure, accidents and breakdowns.',
  '',
  '6. INSURANCE',
  'One Purpose Logistics has GIT cover in place to the value of R2,000,000.',
  'Insurance of the loads during the operation is excluded. The client will take care of insurance.',
  '',
  '7. LIABILITY',
  'One Purpose Logistics will not be held liable for any damages caused to property or surrounds by our cranes, trucks, and trailers.',
  'Structural integrity of all sites remains the responsibility of the client.'
];

const transcorTerms = [
  '1. DEFINITIONS AND INTERPRETATION',
  '1.1. "Border Country/ies" – The Countries which border the Republic of South Africa, including Zimbabwe, Mozambique, Namibia, Botswana, Swaziland, Zambia, DRC and Lesotho.',
  '1.2. "Business day" – any day other than a Saturday, Sunday or public holiday in the RSA.',
  '1.3. "Carrier" – One Purpose Logistics (Pty) Ltd.',
  '1.4. "Customer" – the person to whom the Quote is addressed.',
  '1.5. "Goods" – the products and/or goods to be transported.',
  '1.6. "Services" – the carriage, transportation, handling and/or haulage services.',
  '',
  '2. ACCEPTANCE OF TERMS',
  '2.1. By accepting a Quote or making Goods available for collection, the Customer accepts these terms.',
  '2.2. Any other terms and conditions are expressly excluded.',
  '',
  '3. SERVICES',
  '3.1. The Carrier shall arrange for, plan, implement and control the loading, collection and transportation of the Goods.',
  '',
  '4. CONDITION OF GOODS',
  '4.1. The onus of proving the quantity, type and condition of the Goods at the time of receipt shall remain with the Customer.',
  '',
  '5. DANGEROUS GOODS',
  '5.1. The Customer warrants that all Goods are not dangerous unless declared otherwise in writing.',
  '',
  '6. LOADING AND OFF-LOADING',
  '6.1. The Customer shall ensure safe, suitable and adequate access and loading facilities at all collection and delivery points.',
  '',
  '7. ROUTE',
  '7.1. The Carrier shall in its sole discretion decide what route to follow.',
  '',
  '8. QUOTES',
  '8.1. Quotes exclude any VAT, duties, taxes or outlays.',
  '8.2. The Carrier shall not be bound by any Quote older than 30 days.',
  '',
  '9. INVOICES AND PAYMENT',
  '9.1. Invoices shall be payable within the terms stated on the invoice.',
  '9.2. Interest will accrue on all overdue payments at 18% per annum.',
  '',
  '10. INDEMNITY AND LIMITED LIABILITY',
  '10.1. Goods are carried at the sole risk of the Customer, unless the Carrier is grossly negligent.',
  '10.2. Maximum liability of the Carrier shall be limited to the value of the Goods or double the Carrier\'s fee, whichever is lesser.',
  '',
  '11. INSURANCE',
  '11.1. The Customer shall insure the Goods at its own cost.',
  '11.2. The Carrier has GIT cover to the value of R2,000,000. Should the value exceed this, the Customer must arrange additional cover.'
];

// ============================================
// MAIN COMPONENT
// ============================================
const QuotePDF = ({ quote, variant = 'quote', proforma = null, companyDetails = null, credit = null }) => {
  const company = companyDetails || DEFAULT_COMPANY;
  // Resolve the logo to an absolute URL so pdf().toBlob() can fetch it
  // (relative paths fail in the download flow but work in the create preview).
  const resolveLogo = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('http://') || logo.startsWith('https://') || logo.startsWith('data:')) return logo;
    if (typeof window !== 'undefined') return window.location.origin + logo;
    return logo;
  };
  const logoSrc = resolveLogo(company.company_logo);
  const bank = {
    accountName: company.bank_account_name,
    bank: company.bank_name,
    accountNumber: company.bank_account_number,
    accountType: company.bank_account_type,
    branchCode: company.bank_branch_code,
  };

  if (!quote) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={styles.quoteTitle}>No Quote Data</Text>
        </Page>
      </Document>
    );
  }

  const isMinerals = quote.quote_type === 'minerals';
  const isAgri = quote.quote_type === 'agri';
  const isAbnormal = quote.quote_type === 'abnormal';
  const isGeneral = quote.quote_type === 'general';
  const isProforma = variant === 'proforma';
  const docTitle = isProforma ? 'PROFORMA INVOICE' : 'QUOTATION';

  // Available client credit shown on a proforma: reduces the amount due.
  const grandTotal = parseFloat(quote?.grand_total) || 0;
  const creditAvailable = parseFloat(credit?.total) || 0;
  const creditUsed = isProforma ? Math.min(creditAvailable, Math.max(grandTotal, 0)) : 0;
  const creditRefs = ((credit?.notes) || []).map((n) => n.number).filter(Boolean).join(', ');
  const amountDue = Math.max(grandTotal - creditUsed, 0);

  const lineItems = [];
  if (quote.items && quote.items.length > 0) {
    quote.items.forEach(item => {
      if (item.description) {
        lineItems.push({
          description: item.description,
          qty: item.quantity || 1,
          rate: formatCurrency(item.unit_price || 0),
          amount: formatCurrency(item.total || 0),
        });
      }
    });
  }

  if ((isMinerals || isAgri) && lineItems.length === 0) {
    const truckLabel = getTruckTypeLabel(quote.truck_type);
    const truckDisplay = quote.truck_type === 'other' ? quote.truck_type_other || 'Other' : truckLabel;
    lineItems.push({
      description: `${quote.commodity || 'Commodity'} - ${quote.wet_or_dry === 'wet' ? 'Wet' : 'Dry'} (${truckDisplay || 'Standard'})`,
      qty: quote.tonnage ? `${quote.tonnage} MT` : '-',
      rate: formatCurrency(quote.rate_per_ton || 0),
      amount: formatCurrency((quote.tonnage || 0) * (quote.rate_per_ton || 0)),
    });
  }

  if (isGeneral && lineItems.length === 0) {
    lineItems.push({
      description: quote.services || 'General Services',
      qty: `${quote.estimated_hours || 0} hrs`,
      rate: formatCurrency(quote.hourly_rate || 0),
      amount: formatCurrency((quote.estimated_hours || 0) * (quote.hourly_rate || 0)),
    });
  }

  if (isAbnormal && lineItems.length === 0) {
    lineItems.push({
      description: quote.description || 'Abnormal Load Transport',
      qty: '1',
      rate: formatCurrency(quote.client_price || 0),
      amount: formatCurrency(quote.client_price || 0),
    });
  }

  let termsList = ['standard'];
  if (quote.terms_conditions) {
    if (typeof quote.terms_conditions === 'string') {
      termsList = quote.terms_conditions.split(',').filter(Boolean);
    } else if (Array.isArray(quote.terms_conditions)) {
      termsList = quote.terms_conditions;
    }
  }
  const includeStandard = termsList.includes('standard');
  const includeAbnormal = termsList.includes('abnormal');
  const includeEnm = termsList.includes('enm');
  const includeLovemore = termsList.includes('lovemore');
  const includeBenecke = termsList.includes('benecke');
  const includeTranscor = termsList.includes('transcor');
  const includeCustom = termsList.includes('custom');

  const addressParts = [company.company_address];
  if (company.company_address_line2) addressParts.push(company.company_address_line2);
  const fullAddress = addressParts.join(', ');

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              {logoSrc && <Image src={logoSrc} style={styles.headerLogo} />}
            </View>
            <View style={styles.headerDetails}>
              <Text style={styles.headerDetailText}>{fullAddress}</Text>
              <Text style={styles.headerDetailText}>Tel: {company.company_phone}</Text>
              <Text style={styles.headerDetailText}>Email: {company.company_email}</Text>
              <Text style={styles.headerDetailText}>Web: {company.company_website}</Text>
              <Text style={styles.headerDetailText}>Reg: {company.company_reg_number}</Text>
              {company.company_vat_number && <Text style={styles.headerDetailText}>VAT: {company.company_vat_number}</Text>}
            </View>
          </View>
        </View>

        <View style={styles.content}>
          <Text style={styles.quoteTitle}>{docTitle}</Text>
          {isProforma ? (
            <>
              <View style={styles.infoRow}>
                <View style={styles.infoItem}><Text style={styles.infoLabel}>Proforma Number</Text><Text style={styles.infoValue}>{proforma?.invoice_number || 'N/A'}</Text></View>
                <View style={styles.infoItem}><Text style={styles.infoLabel}>Quote Number</Text><Text style={styles.infoValue}>{quote.quote_number || 'N/A'}</Text></View>
                <View style={styles.infoItem}><Text style={styles.infoLabel}>Job Number</Text><Text style={styles.infoValue}>{proforma?.job_number || 'N/A'}</Text></View>
              </View>
              <View style={styles.infoRow}>
                <View style={styles.infoItem}><Text style={styles.infoLabel}>Proforma Date</Text><Text style={styles.infoValue}>{formatDate(proforma?.date)}</Text></View>
                <View style={styles.infoItem}><Text style={styles.infoLabel}>Collection Date</Text><Text style={styles.infoValue}>{formatDate(proforma?.pickup_date)}</Text></View>
                <View style={styles.infoItem}><Text style={styles.infoLabel}>Type</Text><Text style={styles.infoValue}>{getTypeLabel(quote.quote_type)}</Text></View>
              </View>
            </>
          ) : (
            <View style={styles.infoRow}>
              <View style={styles.infoItem}><Text style={styles.infoLabel}>Quote Number</Text><Text style={styles.infoValue}>{quote.quote_number}</Text></View>
              <View style={styles.infoItem}><Text style={styles.infoLabel}>Date</Text><Text style={styles.infoValue}>{formatDate(quote.created_at)}</Text></View>
              <View style={styles.infoItem}><Text style={styles.infoLabel}>Valid Until</Text><Text style={styles.infoValue}>{formatDate(quote.expiry_date)}</Text></View>
              <View style={styles.infoItem}><Text style={styles.infoLabel}>Type</Text><Text style={styles.infoValue}>{getTypeLabel(quote.quote_type)}</Text></View>
            </View>
          )}
          <View style={styles.divider} />
          <View style={styles.twoCol}>
            <View style={styles.col}>
              <Text style={styles.sectionTitle}>Customer</Text>
              <Text style={styles.sectionText}>{quote.customer_name || 'N/A'}</Text>
              {quote.contact_person && <Text style={styles.sectionText}>Attn: {quote.contact_person}</Text>}
              {quote.contact_phone && <Text style={styles.sectionText}>Tel: {quote.contact_phone}</Text>}
              {quote.contact_email && <Text style={styles.sectionText}>Email: {quote.contact_email}</Text>}
              {/* The customer's own address and VAT number, taken live from their client record. A
                  PROFORMA is rendered from this same document, so this is what puts them on a
                  proforma too. Each line appears only when we actually have the detail. */}
              {quote.customer?.physical_address && <Text style={styles.sectionText}>{quote.customer.physical_address}</Text>}
              {quote.customer?.vat_number && <Text style={styles.sectionText}>VAT: {quote.customer.vat_number}</Text>}
              {quote.customer?.registration_number && <Text style={styles.sectionText}>Reg: {quote.customer.registration_number}</Text>}
            </View>
            <View style={styles.col}>
              <Text style={styles.sectionTitle}>Quote Details</Text>
              <Text style={styles.sectionText}>Type: {getTypeLabel(quote.quote_type)}</Text>
              <Text style={styles.sectionText}>Payment Terms: {quote.payment_terms || '30 Days'}</Text>
              {quote.salesperson?.full_name && <Text style={styles.sectionText}>Salesperson: {quote.salesperson.full_name}</Text>}
              {quote.distance_km && <Text style={styles.sectionText}>Distance: {quote.distance_km} km</Text>}
            </View>
          </View>

          {(quote.origin || quote.route?.pickup_address) && (
            <>
              <View style={styles.divider} />
              <View style={styles.routeBox}>
                <Text style={[styles.sectionTitle, { color: '#0A1929' }]}>Route Information</Text>
                <View style={{ flexDirection: 'row', gap: 20 }}>
                  <View style={{ flex: 1 }}><Text style={{ fontSize: 8, color: '#666666', marginBottom: 2 }}>Pickup</Text><Text style={styles.routeText}>{quote.route?.pickup_address || quote.origin || 'N/A'}</Text></View>
                  <View style={{ flex: 1 }}><Text style={{ fontSize: 8, color: '#666666', marginBottom: 2 }}>Delivery</Text><Text style={styles.routeText}>{quote.route?.delivery_address || quote.destination || 'N/A'}</Text></View>
                </View>
                {quote.distance_km && <View style={{ marginTop: 6 }}><Text style={{ fontSize: 8, color: '#666666' }}>Distance: <Text style={{ fontWeight: 'bold' }}>{quote.distance_km} km</Text></Text></View>}
              </View>
            </>
          )}

          {/* ✅ QUOTE SPECIFICS - Type-specific details above line items */}
          {(isMinerals || isAgri) && (
            <>
              <View style={styles.divider} />
              <View style={styles.twoCol}>
                <View style={styles.col}>
                  <Text style={styles.sectionTitle}>Commodity</Text>
                  <Text style={styles.sectionText}>{quote.commodity || 'N/A'}</Text>
                  <Text style={styles.sectionText}>Tonnage: {quote.tonnage || 0} MT</Text>
                </View>
                <View style={styles.col}>
                  <Text style={styles.sectionTitle}>Specifications</Text>
                  <Text style={styles.sectionText}>Rate: {formatCurrency(quote.rate_per_ton || 0)}/MT</Text>
                  <Text style={styles.sectionText}>Wet / Dry: {quote.wet_or_dry || 'N/A'}</Text>
                  <Text style={styles.sectionText}>Truck: {getTruckTypeLabel(quote.truck_type) || 'N/A'}</Text>
                </View>
              </View>
            </>
          )}

          {isAbnormal && quote.description && (
            <>
              <View style={styles.divider} />
              <View>
                <Text style={styles.sectionTitle}>Job Description</Text>
                <Text style={styles.sectionText}>{quote.description}</Text>
              </View>
            </>
          )}

          {isGeneral && (quote.description || quote.services) && (
            <>
              <View style={styles.divider} />
              <View>
                <Text style={styles.sectionTitle}>Job Description</Text>
                <Text style={styles.sectionText}>{quote.description || quote.services}</Text>
              </View>
            </>
          )}

          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderText, styles.colDescription]}>Description</Text>
              <Text style={[styles.tableHeaderText, styles.colQty]}>Qty</Text>
              <Text style={[styles.tableHeaderText, styles.colRate]}>Rate</Text>
              <Text style={[styles.tableHeaderText, styles.colAmount]}>Amount</Text>
            </View>
            {lineItems.map((item, index) => (
              <View key={index} style={index % 2 === 0 ? styles.tableRow : styles.tableRowEven} wrap={false}>
                <Text style={[styles.tableText, styles.colDescription]}>{item.description}</Text>
                <Text style={[styles.tableText, styles.colQty]}>{item.qty}</Text>
                <Text style={[styles.tableText, styles.colRate]}>{item.rate}</Text>
                <Text style={[styles.tableText, styles.colAmount]}>{item.amount}</Text>
              </View>
            ))}
          </View>

          <View style={{ flexDirection: 'row', gap: 20, marginTop: 10 }} wrap={false}>
            {bank && <View style={styles.paymentBox}>
              <Text style={styles.paymentTitle}>Payment Details</Text>
              <Text style={styles.paymentText}>Account: {bank.accountName}</Text>
              <Text style={styles.paymentText}>Bank: {bank.bank}</Text>
              <Text style={styles.paymentText}>Acc No: {bank.accountNumber}</Text>
              <Text style={styles.paymentText}>Ref: {isProforma ? (proforma?.invoice_number || 'N/A') : (quote.quote_number || 'N/A')}</Text>
            </View>}
            <View style={[styles.totalsBox, { flex: 1 }]}>
              <View style={styles.totalRow}><Text style={styles.totalLabel}>Subtotal</Text><Text style={styles.totalValue}>{formatCurrency(quote.total_amount)}</Text></View>
              {quote.vat_included && quote.vat_amount > 0 && <View style={styles.totalRow}><Text style={styles.totalLabel}>VAT (15%)</Text><Text style={styles.totalValue}>{formatCurrency(quote.vat_amount)}</Text></View>}
              <View style={styles.totalRow}><Text style={styles.totalLabel}>Total</Text><Text style={styles.totalValue}>{formatCurrency(grandTotal)}</Text></View>
              {creditUsed > 0 && (
                <View style={styles.totalRow}><Text style={styles.totalLabel}>Less credit{creditRefs ? ` (${creditRefs})` : ''}</Text><Text style={styles.totalValue}>-{formatCurrency(creditUsed)}</Text></View>
              )}
              <View style={styles.totalRowFinal}>
                <Text style={styles.totalLabelFinal}>{creditUsed > 0 ? 'AMOUNT DUE' : 'TOTAL'}</Text>
                <Text style={styles.totalValueFinal}>{formatCurrency(creditUsed > 0 ? amountDue : grandTotal)}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>{company.company_name} │ {fullAddress} │ Tel: {company.company_phone}</Text>
          <Text style={[styles.footerText, { marginTop: 3 }]}>Reg: {company.company_reg_number}</Text>
        </View>
      </Page>

      {(includeStandard || includeAbnormal || includeEnm || includeLovemore || includeBenecke || includeTranscor || includeCustom) && (
        <Page size="A4" style={styles.termsPage}>
          <View style={styles.termsContent}>
            <Text style={styles.termsTitle}>Terms and Conditions</Text>

            {includeStandard && <View style={styles.termsSection}>
              <Text style={styles.termsSectionTitle}>STANDARD TERMS AND CONDITIONS</Text>
              {standardTerms.map((t, i) => <Text key={i} style={styles.termsText}>{t}</Text>)}
            </View>}

            {includeAbnormal && <View style={styles.termsSection}>
              <Text style={styles.termsSectionTitle}>ABNORMAL LOAD TERMS</Text>
              {abnormalTerms.map((t, i) => <Text key={i} style={styles.termsText}>{t}</Text>)}
            </View>}

            {includeEnm && <View style={styles.termsSection}>
              <Text style={styles.termsSectionTitle}>TERMS AND CONDITIONS</Text>
              {enmTerms.map((t, i) => t ? <Text key={i} style={styles.termsText}>{t}</Text> : null)}
            </View>}

            {includeLovemore && <View style={styles.termsSection}>
              <Text style={styles.termsSectionTitle}>TERMS AND CONDITIONS</Text>
              {lovemoreTerms.map((t, i) => t ? <Text key={i} style={styles.termsText}>{t}</Text> : null)}
            </View>}

            {includeTranscor && <View style={styles.termsSection}>
              <Text style={styles.termsSectionTitle}>TERMS AND CONDITIONS</Text>
              {transcorTerms.map((t, i) => t ? <Text key={i} style={styles.termsText}>{t}</Text> : null)}
            </View>}

            {includeBenecke && <View style={styles.termsSection}>
              <Text style={styles.termsSectionTitle}>TERMS AND CONDITIONS</Text>
              {beneckeTerms.map((t, i) => t ? <Text key={i} style={styles.termsText}>{t}</Text> : null)}
            </View>}

            {includeCustom && quote.custom_terms && <View style={styles.termsSection}>
              <Text style={styles.termsSectionTitle}>CUSTOM TERMS</Text>
              <Text style={styles.termsText}>{quote.custom_terms}</Text>
            </View>}
          </View>
          <View style={styles.footer} fixed>
            <Text style={styles.footerText}>{company.company_name} │ {fullAddress} │ Tel: {company.company_phone}</Text>
            <Text style={[styles.footerText, { marginTop: 3 }]}>Reg: {company.company_reg_number}</Text>
          </View>
        </Page>
      )}
    </Document>
  );
};

export default QuotePDF;